// Frontend mirror of the INSAION RBAC matrix. The string values MUST stay in
// sync with migrations/V26__rbac_permission_matrix.sql and the Go constants in
// api/internal/middleware/permissions.go.

export const PERMISSIONS = {
  ORG_BILLING: "org:billing",
  ORG_USERS_READ: "org:users:read",
  ORG_USERS_WRITE: "org:users:write",
  DEVICE_WRITE: "device:write",
  DEVICE_READ: "device:read",
  DASHBOARD_WRITE: "dashboard:write",
  DASHBOARD_READ: "dashboard:read",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export type Role = "Admin" | "Member" | "Viewer";

// Role → permission mapping, mirroring the backend seed. Used to derive the
// user's role from the flat permissions array carried in the JWT, since the
// access token only injects the resolved permissions.
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  Admin: [
    PERMISSIONS.ORG_BILLING,
    PERMISSIONS.ORG_USERS_READ,
    PERMISSIONS.ORG_USERS_WRITE,
    PERMISSIONS.DEVICE_WRITE,
    PERMISSIONS.DEVICE_READ,
    PERMISSIONS.DASHBOARD_WRITE,
    PERMISSIONS.DASHBOARD_READ,
  ],
  Member: [
    PERMISSIONS.ORG_USERS_READ,
    PERMISSIONS.DEVICE_WRITE,
    PERMISSIONS.DEVICE_READ,
    PERMISSIONS.DASHBOARD_WRITE,
    PERMISSIONS.DASHBOARD_READ,
  ],
  Viewer: [PERMISSIONS.DEVICE_READ, PERMISSIONS.DASHBOARD_READ],
};

/**
 * hasPermission returns true when `granted` contains every permission in
 * `required`. With no required permissions it returns true (open access).
 */
export function hasPermission(
  granted: readonly string[] | null | undefined,
  required: Permission | Permission[],
): boolean {
  const needed = Array.isArray(required) ? required : [required];
  if (needed.length === 0) return true;
  if (!granted || granted.length === 0) return false;
  const set = new Set(granted);
  return needed.every((p) => set.has(p));
}

/**
 * deriveRole infers the user's role from their flat permissions. Highest
 * privilege wins; defaults to "Viewer" when nothing matches. Org-level
 * permissions identify an Admin, device/dashboard write identifies a Member.
 */
export function deriveRole(
  granted: readonly string[] | null | undefined,
): Role {
  const set = new Set(granted ?? []);
  if (
    set.has(PERMISSIONS.ORG_USERS_WRITE) ||
    set.has(PERMISSIONS.ORG_BILLING)
  ) {
    return "Admin";
  }
  if (
    set.has(PERMISSIONS.DEVICE_WRITE) ||
    set.has(PERMISSIONS.DASHBOARD_WRITE)
  ) {
    return "Member";
  }
  return "Viewer";
}
